/**
 * SourceRenderer — dispatch on `kind` to a lazy-loaded native renderer.
 *
 * Every renderer is React.lazy so the chat bundle never carries pdfjs,
 * DOMPurify, or any of the structured tables until the user actually
 * clicks a chip.
 *
 * PR1: only PDF-family kinds rendered; everything else fell back to
 * RendererPlaceholder. PR2 fills out every kind.
 */

import { lazy, Suspense, type LazyExoticComponent, type ComponentType } from 'react'
import { Loader2 } from 'lucide-react'
import type { Citation, SourceKind } from '../../types/cards'

type RendererComponent = LazyExoticComponent<ComponentType<{ citation: Citation }>>

// ── Lazy renderers ──────────────────────────────────────────────────────────
// PDF family
const EpicorPdfRenderer  = lazy(() => import('./renderers/EpicorPdfRenderer'))
const SpecSheetRenderer  = lazy(() => import('./renderers/SpecSheetRenderer'))
const ShopDrawingRenderer = lazy(() => import('./renderers/ShopDrawingRenderer'))
const VendorDocRenderer  = lazy(() => import('./renderers/VendorDocRenderer'))

// Structured (non-PDF)
const PackSlipRenderer       = lazy(() => import('./renderers/PackSlipRenderer'))
const InvoiceDepRenderer     = lazy(() => import('./renderers/InvoiceDepRenderer'))
const InvoiceShpRenderer     = lazy(() => import('./renderers/InvoiceShpRenderer'))
const ProFormaRenderer       = lazy(() => import('./renderers/ProFormaRenderer'))
const BaqResultRenderer      = lazy(() => import('./renderers/BaqResultRenderer'))
const PlaidTransactionsRenderer = lazy(() => import('./renderers/PlaidTransactionsRenderer'))
const EmailRenderer          = lazy(() => import('./renderers/EmailRenderer'))
const SalesOrderRenderer     = lazy(() => import('./renderers/SalesOrderRenderer'))

// Fallback (unknown SourceKind values, including future additions)
const RendererPlaceholder = lazy(() => import('./renderers/RendererPlaceholder'))

// ── Dispatch table ──────────────────────────────────────────────────────────
const REGISTRY: Record<SourceKind, RendererComponent> = {
  epicor_pdf:         EpicorPdfRenderer,
  spec_sheet:         SpecSheetRenderer,
  shop_drawing:       ShopDrawingRenderer,
  vendor_doc:         VendorDocRenderer,
  generic_pdf:        EpicorPdfRenderer, // reuse — same shape, generic chrome
  pack_slip:          PackSlipRenderer,
  invoice_dep:        InvoiceDepRenderer,
  invoice_shp:        InvoiceShpRenderer,
  pro_forma:          ProFormaRenderer,
  baq_result:         BaqResultRenderer,
  list_result:        BaqResultRenderer, // reuse — same table, human title + humanized headers
  plaid_transactions: PlaidTransactionsRenderer,
  email:              EmailRenderer,
  sales_order:        SalesOrderRenderer,
}

export default function SourceRenderer({ citation }: { citation: Citation }) {
  const Component = REGISTRY[citation.kind] ?? RendererPlaceholder
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Component citation={citation} />
    </Suspense>
  )
}

function LoadingFallback() {
  return (
    <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
      <Loader2 size={16} className="animate-spin" />
      Loading viewer…
    </div>
  )
}
